import {
  createContext,
  type PropsWithChildren,
  useContext,
  useState,
  useRef,
} from 'react'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import Cookies from 'js-cookie'

import { getReturnTo, onLogin } from '../utils/login/on-login'
import { shouldSkipAuthRedirect } from '../utils/auth/paths-helper'
import { COOKIE_KEYS } from '../utils/auth/cookies'
import { devLog } from '../utils/devLogger'
import { logNewRelicEvent } from '../utils/common/custom/newrelic-page-events'

import { getRefreshedTokenFromServer } from '@/root/utils/terminalV0-utils/auth-helpers'
import LogoLoader from '@/components/Common/Loader/LogoLoader'

interface AuthContextType {
  isLoading: boolean
  token: string | null
  onInvalidAccess: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({
  children,
  host,
}: PropsWithChildren<{ host: string }>) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const router = useRouter()
  const isRefreshingRef = useRef(false)

  const [token, setTokenState] = useState<string | null>(
    () => Cookies.get(COOKIE_KEYS.TOKEN) ?? null,
  )
  const [ignored, setRefreshTokenState] = useState<string | null>(
    () => Cookies.get(COOKIE_KEYS.REFRESH_TOKEN) ?? null,
  )

  const logoutUser = () => {
    setTokenState(null)
    setRefreshTokenState(null)

    if (shouldSkipAuthRedirect(pathname)) {
      // No redirection for these paths
      return
    }

    // Build return_to URL with current pathname and search params
    const returnTo = getReturnTo(pathname, searchParams)

    router.push(`/api/auth/logout?return_to=${returnTo}`)
  }

  const onInvalidAccess = async () => {
    const currentToken = Cookies.get(COOKIE_KEYS.TOKEN)
    if (!currentToken || isRefreshingRef.current) {
      devLog('Token not found, skipping refresh')
      logNewRelicEvent('refresh_token_blocked', {
        traceId: Cookies.get(COOKIE_KEYS.TRACE_ID) ?? '',
        source: isRefreshingRef.current ? 'is_loading' : 'no_token',
        appType: 'stencil',
      })
      return
    }

    isRefreshingRef.current = true
    setTokenState(null)

    const refreshResponse = await getRefreshedTokenFromServer().catch(() => {
      console.info('clearTokensAndRedirectToLogin')
      logoutUser()
      return null
    })
    if (refreshResponse?.authCode) {
      onLogin(getReturnTo(pathname, searchParams), {
        auth_code: refreshResponse.authCode ?? '',
      })
    } else {
      setTokenState(Cookies.get(COOKIE_KEYS.TOKEN) ?? null)
    }
    isRefreshingRef.current = false
  }

  const value = {
    isLoading: isRefreshingRef.current,
    token,
    onInvalidAccess,
  }

  if (isRefreshingRef.current) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
        <LogoLoader host={host} size="lg" showText={true} />
      </div>
    )
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
